import * as React from 'react';
import * as moment from 'moment';
import * as qs from 'query-string';
import { connect, Dispatch } from 'react-redux';
import { CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { Button, FormControl } from 'react-bootstrap';
import { IApplicationState, AppActionCreators, RccapActionCreators, ISwapFailureRateReportResponse } from '@nkr/store/index';
import { IRpSwapFailureRate } from '@nkr/models/center-performance/relative-performance';
import { ISwapFailureRateReportRow } from '@nkr/models/index';
import { RccapStatus } from '@nkr/enums/index';
import AuditReportScoreTable from '@nkr/views/reports/audit-report-score-table';
import { SwapFailureRateOverlay } from '@nkr/views/center-admin/action-center/center-performance/relative-performance-overlays';
import RccapGrid from './rccap-grid';

interface IOwnProps {
    centerId: number
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getSwapFailureRateReport: (centerId: number, startDate: string, endDate: string) => Promise<ISwapFailureRateReportResponse>
}

type TProps = IDispatchProps & IOwnProps;

interface IState {
    startDate: string,
    endDate: string,
    report: ISwapFailureRateReportRow[],
    swapFailureRate: IRpSwapFailureRate,
    gridApi: GridApi
}

const getEmptySwapFailureRate = (): IRpSwapFailureRate => ({
    rank: 0,
    failures: 0,
    covidFailures: 0,
    totalTransplants: 0,
    failureRate: 0,
    category: 'NV'
});

class RccapList extends React.Component<TProps, IState> {
    readonly state: IState = {
        startDate: '',
        endDate: '',
        report: [],
        swapFailureRate: getEmptySwapFailureRate(),
        gridApi: null
    }

    componentDidMount = () => {
        if (!this.props.centerId || Number.isNaN(this.props.centerId)) {
            return;
        }

        const parsed = qs.parse(location.search);
        let startDate = moment().add(-12, 'month').format('YYYY-MM-DD');
        let endDate = moment().format('YYYY-MM-DD');

        if (parsed.sd) {
            startDate = parsed.sd as string;
        }

        if (parsed.ed) {
            endDate = parsed.ed as string;
        }

        this.getData(startDate, endDate);
    }

    componentDidUpdate = (prevProps: TProps) => {
        if ((!prevProps.centerId || Number.isNaN(prevProps.centerId)) && this.props.centerId && !Number.isNaN(this.props.centerId)) {
            const parsed = qs.parse(location.search);
            const startDate = parsed.sd ? parsed.sd as string : moment().add(-12, 'month').format('YYYY-MM-DD');
            const endDate = parsed.ed ? parsed.ed as string : moment().format('YYYY-MM-DD');

            this.getData(startDate, endDate);
        }
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });
    }

    getData = (startDate: string, endDate: string) => {
        if (!this.props.centerId || Number.isNaN(this.props.centerId)) {
            return;
        }

        this.props.showLoading('Getting swap failure rate report, one moment please...').then(() => {
            this.props.getSwapFailureRateReport(this.props.centerId, startDate, endDate)
                .then(response => {
                    this.setState({
                        startDate,
                        endDate,
                        report: response.data.results.data,
                        swapFailureRate: response.data.results.swapFailureRate ?? getEmptySwapFailureRate()
                    }, () => {
                        this.setUrlParameters();
                    });
                })
                .finally(() => {
                    this.props.hideLoading();
                });
        });
    }

    setUrlParameters = () => {
        const { startDate, endDate } = this.state;
        const params = [`sd=${startDate}`, `ed=${endDate}`].join('&');
        const newUrl = `/center/${this.props.centerId}/reports/my-center/rccap?${params}`;

        window.history.pushState({}, '', newUrl);
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        const { name, value } = e.currentTarget;

        this.setState({
            [name]: value
        } as Pick<IState, 'startDate' | 'endDate'>);
    }

    handleDateRangeSearch = () => {
        this.getData(this.state.startDate, this.state.endDate);
    }

    handleRespond = (id: number) => {
        window.location.href = `/center/${this.props.centerId}/action-center/rccap-respond/${id}`;
    }

    handleView = (id: number) => {
        window.location.href = `/center/${this.props.centerId}/action-center/rccap-view/${id}`;
    }

    handleExport = () => {
        if (this.state.gridApi == null) {
            return;
        }

        const params: CsvExportParams = {
            columnGroups: true,
            fileName: 'swap-failure-rate-last-12-months.csv',
            processCellCallback: (params: ProcessCellForExportParams) => {
                if (params.column.getColDef().colId == 'rank') {
                    return params.node.rowIndex + 1;
                }

                if (params.column.getColDef().colId == 'swapNumber') {
                    const data = params.node.data as ISwapFailureRateReportRow;
                    return `${data.swapChainNumber ?? ''}/${data.swapClusterNumber ?? ''}/${data.swapPositionNumber ?? ''}`;
                }

                if (params.column.getColDef().colId == 'buttons') {
                    const data = params.node.data as ISwapFailureRateReportRow;

                    if (data.outcome != 'Failed') {
                        return '';
                    }

                    return data.currentStatusId == RccapStatus.PendingResponse
                        || data.currentStatusId == RccapStatus.NeedsMoreInfo
                        ? 'Respond'
                        : 'View RCCAP';
                }

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    render = () => {
        const swapFailureRate = this.state.swapFailureRate ?? getEmptySwapFailureRate();

        return (
            <>
                <div className='well well-sm' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>Start Date:&nbsp;</div>
                            <div>
                                <FormControl
                                    type='date'
                                    name='startDate'
                                    value={this.state.startDate}
                                    onChange={this.handleDateChange}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                            <div style={{ marginRight: 5 }}>End Date:</div>
                            <div>
                                <FormControl
                                    type='date'
                                    name='endDate'
                                    value={this.state.endDate}
                                    onChange={this.handleDateChange}
                                />
                            </div>
                        </div>

                        <div>
                            <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>
                        </div>
                    </div>
                    <div>
                        <AuditReportScoreTable
                            reportName='Swap Failure Rate Last 12 months'
                            scoreValue={`${Math.round(swapFailureRate.failureRate)}%`}
                            overlay={SwapFailureRateOverlay({ swapFailureRate })}
                        />
                    </div>
                    <div>
                        <Button onClick={this.handleExport}>Export CSV</Button>
                    </div>
                </div>

                <RccapGrid
                    list={this.state.report}
                    onGridReady={this.onGridReady}
                    onRespond={this.handleRespond}
                    onView={this.handleView}
                />
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getSwapFailureRateReport: (centerId, startDate, endDate) => {
            return dispatch(RccapActionCreators.getSwapFailureRateReport(centerId, startDate, endDate));
        }
    };
}

export default connect<null, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(RccapList);
